import clsx from 'clsx';
import React, { useCallback, useEffect } from 'react';
import ScaleIcon from '~/icons/scale.svg';
import { Typography } from '~/shared/components';
import {
    comparisonCopy,
    useCompareModeStore,
    useHasComparisonItems,
    useIsProductComparisonEnabled,
    useProductComparisonStore,
} from '~/shared/productComparison';
import styles from './CompareModeToggle.module.scss';

/**
 * Mobile-only entry point for product comparison, shown next to the filter button. There
 * isn't room to show a compare toggle on every card by default on mobile, so switching this
 * on is what reveals them - matches the filter button's own trigger/drawer pattern.
 */
export const CompareModeToggle: React.FC = () => {
    const isEnabled = useIsProductComparisonEnabled();
    const isActive = useCompareModeStore((state) => state.isActive);
    const toggle = useCompareModeStore((state) => state.actions.toggle);
    const setIsActive = useCompareModeStore((state) => state.actions.setIsActive);
    const clear = useProductComparisonStore((state) => state.clear);
    const hasComparisonItems = useHasComparisonItems();

    // The selection survives a refresh but the mode doesn't - switch it back on, or the cards
    // lose their toggles while the tray still shows the selection.
    useEffect(() => {
        if (hasComparisonItems) setIsActive(true);
    }, [hasComparisonItems, setIsActive]);

    const onClick = useCallback(() => {
        // Switching the mode back off has the same effect as pressing "Ryd" in the tray -
        // leaving a stale selection around after the toggles disappear would be confusing.
        if (isActive) clear();

        toggle();
    }, [isActive, clear, toggle]);

    if (!isEnabled) return null;

    return (
        <button
            type="button"
            className={clsx(styles.toggle, isActive && styles.isActive)}
            onClick={onClick}
            aria-pressed={isActive}
        >
            <ScaleIcon className={styles.icon} aria-hidden="true" />
            <Typography as="span" variant="paragraph-M" weight="medium">
                {comparisonCopy.add}
            </Typography>
        </button>
    );
};
